// src/services/AuthService.js
//
// Manages SIWE (Sign-In With Ethereum) auth lifecycle.
//
// Flow:
//   1. login(walletAddress, signMessageAsync) — full SIWE flow
//      a. Fetch nonce from /siwe-nonce
//      b. Construct SIWE message
//      c. Prompt wallet to sign
//      d. POST to /siwe-verify to get JWT
//      e. Store JWT
//
//   2. getToken() — returns current JWT or null. Pure read.
//   3. isAuthenticated(walletAddress) — JWT exists, not expired, wallet matches
//   4. logout() — clears stored JWT
//   5. setEmailSession() — an email sign-in (mission 007, email-session): the
//      same JWT shape, bound to the account's managed address; the session
//      records method 'email' and the email. getSession() returns either.
//      subscribe() notifies on every session change (login, email, logout).
//
// Storage: JWT lives in memory (primary) + localStorage (backup so refreshes,
// new tabs, and browser restarts do NOT re-prompt a signature). Expiry is
// enforced client-side in getToken() and server-side by the JWT's exp claim,
// which bounds the XSS-exfiltration window. (Previously sessionStorage-only,
// which forced a fresh SIWE signature per tab/restart — see P1.1.)
//
// IMPORTANT: SIWE messages require the wallet address in EIP-55 checksum
// format (mixed case). We use that format in the SIWE message itself, but
// lowercase the address everywhere else for consistent comparison/storage.
//
// SMART WALLET NOTE (P-onboarding): the primary onboarding path is
// Coinbase Smart Wallet (passkey, ERC-4337). Its signatures are CONTRACT
// signatures (ERC-1271, wrapped as ERC-6492 pre-deployment). siwe-verify
// handles this: it verifies via viem's RPC-backed
// publicClient.verifyMessage, which covers EOA + 1271 + 6492
// transparently. Keep that property if siwe-verify is ever rewritten —
// bare ecrecover would reject every fresh passkey wallet.

import { SiweMessage } from 'siwe';
import { getAddress } from 'viem';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const STORAGE_KEY = 'lunexx_auth_token';
const EXPIRY_BUFFER_SECONDS = 60;

// In-memory cache. Cleared on hard refresh; restored from localStorage.
let memoryToken = null;
let memoryWallet = null;
let memoryExpiresAt = null;
let memoryMethod = null;   // 'wallet' | 'email'
let memoryEmail = null;

// session-change listeners (useAuthSession); notified after the change, off
// the current render
const listeners = new Set();
let snapshot = null;
function notify() {
  snapshot = null;
  const run = () => listeners.forEach((fn) => { try { fn(); } catch { /* a listener's own problem */ } });
  if (typeof queueMicrotask === 'function') queueMicrotask(run); else run();
}

// ============================================
// Storage
// ============================================

function parseStored(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.walletAddress || !parsed?.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function loadFromSession() {
  try {
    const stored = parseStored(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
    // Migrate a legacy sessionStorage token (pre-P1.1) so existing sessions
    // survive the storage switch without a re-sign.
    const legacy = parseStored(sessionStorage.getItem(STORAGE_KEY));
    if (legacy) {
      saveToSession(legacy.token, legacy.walletAddress, legacy.expiresAt);
      sessionStorage.removeItem(STORAGE_KEY);
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

function saveToSession(token, walletAddress, expiresAt, method = 'wallet', email = null) {
  try {
    // the pre-007 shape (no method) is a wallet session
    localStorage.setItem(STORAGE_KEY, JSON.stringify(method === 'email'
      ? { token, walletAddress, expiresAt, method, email }
      : { token, walletAddress, expiresAt }));
  } catch {
    // storage may be unavailable (private mode, quota); memory cache still works
  }
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

function hydrate() {
  if (memoryToken) return;
  const cached = loadFromSession();
  if (cached) {
    memoryToken = cached.token;
    memoryWallet = cached.walletAddress;
    memoryExpiresAt = cached.expiresAt;
    memoryMethod = cached.method === 'email' ? 'email' : 'wallet';
    memoryEmail = cached.method === 'email' ? cached.email ?? null : null;
  }
}

// ============================================
// SIWE message construction
// ============================================

function buildSiweMessage(checksummedAddress, nonce, chainId = 84532) {
  // SIWE library expects:
  //   - domain: hostname only, no protocol (e.g. "app.lunexx.ai")
  //   - uri: full origin (e.g. "https://app.lunexx.ai")
  //   - address: EIP-55 CHECKSUMMED (mixed case)
  const uri = window.location.origin;
  const domain = window.location.host;

  const message = new SiweMessage({
    domain,
    address: checksummedAddress, // MUST be EIP-55 checksummed
    statement: 'Sign in to Lunexx. This will not trigger any blockchain transaction or cost gas.',
    uri,
    version: '1',
    chainId,
    nonce,
    issuedAt: new Date().toISOString(),
    expirationTime: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
  });

  return message.prepareMessage();
}

// ============================================
// API calls
// ============================================

async function fetchNonce(walletAddressLowercase) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/siwe-nonce`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ walletAddress: walletAddressLowercase }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Nonce request failed (${res.status})`);
  }

  const { nonce } = await res.json();
  if (!nonce) throw new Error('Nonce missing from response');
  return nonce;
}

async function verifySignature(message, signature) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/siwe-verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    // The server provisions the users row for the verified wallet; a
    // referral code captured on landing travels with the sign-in.
    body: JSON.stringify({ message, signature, referralCode: storedReferralCode() }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Verification failed (${res.status})`);
  }

  const { token, walletAddress, expiresIn, user } = await res.json();
  if (!token || !walletAddress || !expiresIn) {
    throw new Error('Invalid verify response');
  }

  return { token, walletAddress, expiresAt: Math.floor(Date.now() / 1000) + expiresIn, user: user ?? null };
}

export function storedReferralCode() {
  try {
    return localStorage.getItem('lunexx_referral_code') ||
      sessionStorage.getItem('lunexx_referral_code') ||
      localStorage.getItem('lunexx_ref') || null;
  } catch {
    return null;
  }
}

// ============================================
// Public API
// ============================================

class AuthService {
  /**
   * Run the full SIWE login flow.
   * Returns { token, walletAddress, expiresAt } on success.
   * Throws on any step failure. If user rejects signing, error.cancelled === true.
   */
  async login(walletAddress, signMessageAsync, options = {}) {
    if (!walletAddress) throw new Error('walletAddress is required');
    if (typeof signMessageAsync !== 'function') {
      throw new Error('signMessageAsync function is required');
    }

    // Normalize to two forms:
    //   - checksummed: required by SIWE library in the message body
    //   - lowercase:   used for storage, comparison, and our DB
    let checksummedAddress;
    try {
      checksummedAddress = getAddress(walletAddress);
    } catch (err) {
      throw new Error('Invalid wallet address format');
    }
    const lowercaseAddress = checksummedAddress.toLowerCase();

    const chainId = options.chainId || 84532;

    // Step 1: get fresh nonce (use lowercase — that's what server stores)
    const nonce = await fetchNonce(lowercaseAddress);

    // Step 2: construct SIWE message (with CHECKSUMMED address)
    const message = buildSiweMessage(checksummedAddress, nonce, chainId);

    // Step 3: prompt user to sign
    let signature;
    try {
      signature = await signMessageAsync({ message });
    } catch (err) {
      if (err?.code === 4001 || /reject|deny|cancel/i.test(err?.message || '')) {
        const cancelled = new Error('Signing cancelled');
        cancelled.cancelled = true;
        throw cancelled;
      }
      throw err;
    }

    if (!signature || !signature.startsWith('0x')) {
      throw new Error('Signer returned invalid signature');
    }

    // Step 4: verify signature server-side, receive JWT
    const result = await verifySignature(message, signature);

    if (result.walletAddress.toLowerCase() !== lowercaseAddress) {
      throw new Error('Returned wallet does not match expected');
    }

    // Step 5: store (always lowercase in our state)
    memoryToken = result.token;
    memoryWallet = result.walletAddress.toLowerCase();
    memoryExpiresAt = result.expiresAt;
    memoryMethod = 'wallet';
    memoryEmail = null;
    saveToSession(memoryToken, memoryWallet, memoryExpiresAt);
    notify();

    // Let wallet-level state (WalletContext) pick up the provisioned user
    // without another round trip.
    try {
      window.dispatchEvent(new CustomEvent('lunexx:signed-in', {
        detail: { walletAddress: memoryWallet, user: result.user },
      }));
    } catch { /* non-browser */ }

    return {
      token: memoryToken,
      walletAddress: memoryWallet,
      expiresAt: memoryExpiresAt,
    };
  }

  /**
   * Store an email-session result ({ token, walletAddress, expiresIn, user,
   * email }) as the active session (method 'email').
   */
  setEmailSession({ token, walletAddress, expiresIn, user = null, email = null }) {
    if (!token || !/^0x[0-9a-fA-F]{40}$/.test(walletAddress || '') || !(expiresIn > 0)) {
      throw new Error('Invalid email session');
    }
    memoryToken = token;
    memoryWallet = walletAddress.toLowerCase();
    memoryExpiresAt = Math.floor(Date.now() / 1000) + expiresIn;
    memoryMethod = 'email';
    memoryEmail = email || null;
    saveToSession(memoryToken, memoryWallet, memoryExpiresAt, 'email', memoryEmail);
    notify();
    try {
      window.dispatchEvent(new CustomEvent('lunexx:signed-in', {
        detail: { walletAddress: memoryWallet, user, method: 'email' },
      }));
    } catch { /* non-browser */ }
    return this.getSession();
  }

  /**
   * The active, unexpired session { token, walletAddress, expiresAt, method,
   * email } or null.
   */
  getSession() {
    if (!this.getToken()) return null;
    return { token: memoryToken, walletAddress: memoryWallet, expiresAt: memoryExpiresAt, method: memoryMethod || 'wallet', email: memoryEmail };
  }

  /** a stable snapshot of getSession() for useSyncExternalStore */
  getSessionSnapshot() {
    if (snapshot === null) snapshot = { value: this.getSession() };
    return snapshot.value;
  }

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  /**
   * Returns current JWT if valid for this wallet, otherwise null.
   * Does NOT trigger login.
   */
  getToken(walletAddress) {
    hydrate();
    if (!memoryToken) return null;
    if (!memoryExpiresAt || memoryExpiresAt - EXPIRY_BUFFER_SECONDS < Math.floor(Date.now() / 1000)) {
      this.logout();
      return null;
    }
    if (walletAddress && memoryWallet !== walletAddress.toLowerCase()) {
      return null;
    }
    return memoryToken;
  }

  /**
   * Returns true if there's a valid, non-expired token for the given wallet.
   */
  isAuthenticated(walletAddress) {
    return this.getToken(walletAddress) !== null;
  }

  /**
   * Time until token expires, in seconds. 0 if expired/missing.
   */
  secondsUntilExpiry() {
    hydrate();
    if (!memoryExpiresAt) return 0;
    return Math.max(0, memoryExpiresAt - Math.floor(Date.now() / 1000));
  }

  /**
   * Clear stored credentials.
   */
  logout() {
    const had = memoryToken !== null || snapshot?.value != null;
    memoryToken = null;
    memoryWallet = null;
    memoryExpiresAt = null;
    memoryMethod = null;
    memoryEmail = null;
    clearSession();
    if (had) notify();
  }

  /**
   * Returns the wallet address bound to the current session, or null.
   */
  getSessionWallet() {
    hydrate();
    return memoryWallet;
  }
}

// A user-initiated wallet connect (a wallet picked in a connect modal or
// button). AuthContext signs in after a connect only within this window, so a
// connection wagmi restores by itself never pops a signature request.
let connectIntentAt = 0;
export function markConnectIntent() { connectIntentAt = Date.now(); }
export function hasConnectIntent(maxAgeMs = 120_000) {
  return connectIntentAt > 0 && Date.now() - connectIntentAt <= maxAgeMs;
}
export function clearConnectIntent() { connectIntentAt = 0; }

export const authService = new AuthService();
export default authService;